import React, { useRef, useEffect, useCallback } from 'react';

function ImageCompareSlider({ originalImageUrl, optimizedImageUrl, imageName, originalSize, optimizedSize, savingsPercent }) {
  const containerRef = useRef(null);
  const originalContainerRef = useRef(null);
  const sliderHandleRef = useRef(null);
  const originalImgRef = useRef(null);
  const optimizedImgRef = useRef(null);
  const isDraggingRef = useRef(false);

  const updateSlider = useCallback((percentage) => {
    if (!originalContainerRef.current || !sliderHandleRef.current) return;
    
    percentage = Math.max(0, Math.min(100, percentage));
    originalContainerRef.current.style.width = percentage + '%';
    sliderHandleRef.current.style.left = percentage + '%';
    
    // Adjust original image width to match container
    if (originalImgRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      originalImgRef.current.style.width = containerWidth + 'px';
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    updateSlider(percentage);
  }, [updateSlider]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleSliderMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  const handleContainerClick = useCallback((e) => {
    if (e.target.closest('.compare-slider-handle')) return;
    handleMouseMove(e);
  }, [handleMouseMove]);

  useEffect(() => {
    // Initialize slider at 50%
    updateSlider(50);
    
    // Function to sync image sizes
    const syncImageSizes = () => {
      if (!containerRef.current || !originalImgRef.current) return;
      
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      
      // Set original image width to match container (for proper clipping)
      if (originalImgRef.current) {
        originalImgRef.current.style.width = containerWidth + 'px';
        originalImgRef.current.style.height = containerHeight + 'px';
      }
      if (optimizedImgRef.current) {
        optimizedImgRef.current.style.width = containerWidth + 'px';
        optimizedImgRef.current.style.height = containerHeight + 'px';
      }
    };
    
    // Sync on mount and when images load
    syncImageSizes();
    
    // Use ResizeObserver to sync when container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        syncImageSizes();
        // Re-update slider position to ensure proper width
        if (originalContainerRef.current && sliderHandleRef.current && containerRef.current) {
          const currentWidth = originalContainerRef.current.style.width;
          const containerWidth = containerRef.current.offsetWidth;
          if (currentWidth && containerWidth > 0) {
            const currentPercentage = (parseFloat(currentWidth) / containerWidth) * 100;
            if (!isNaN(currentPercentage) && currentPercentage >= 0 && currentPercentage <= 100) {
              updateSlider(currentPercentage);
            } else {
              updateSlider(50);
            }
          } else {
            updateSlider(50);
          }
        }
      });
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    // Also sync when images load
    const handleImageLoad = () => {
      syncImageSizes();
    };
    
    const optimizedImg = containerRef.current?.querySelector('.compare-slider-background');
    if (optimizedImg) {
      optimizedImg.addEventListener('load', handleImageLoad);
    }
    
    if (originalImgRef.current) {
      originalImgRef.current.addEventListener('load', handleImageLoad);
    }
    
    return () => {
      resizeObserver.disconnect();
      if (optimizedImg) {
        optimizedImg.removeEventListener('load', handleImageLoad);
      }
      if (originalImgRef.current) {
        originalImgRef.current.removeEventListener('load', handleImageLoad);
      }
    };
  }, [updateSlider]);

  return (
    <div className="compare-slider-container" ref={containerRef} onClick={handleContainerClick}>
      {/* Optimized Image (Background) */}
      <img
        ref={optimizedImgRef}
        src={optimizedImageUrl}
        alt="Optimized"
        className="compare-slider-background"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      
      {/* Original Image (Foreground - clipped) */}
      <div className="compare-slider-foreground" ref={originalContainerRef}>
        <img
          ref={originalImgRef}
          src={originalImageUrl}
          alt="Original"
          className="compare-slider-original"
          onLoad={(e) => {
            if (containerRef.current) {
              const containerWidth = containerRef.current.offsetWidth;
              const containerHeight = containerRef.current.offsetHeight;
              e.target.style.width = containerWidth + 'px';
              e.target.style.height = containerHeight + 'px';
              // Trigger updateSlider to ensure proper positioning
              updateSlider(50);
            }
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      
      {/* Slider Handle */}
      <div
        className="compare-slider-handle"
        ref={sliderHandleRef}
        onMouseDown={handleSliderMouseDown}
      >
        <div className="compare-slider-handle-line">
          <div className="compare-slider-handle-circle">
            <div className="compare-slider-handle-icon">⟷</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCompareSlider;

